import { BASEMAP_STYLES } from "../constants";
import {
  formatBool01,
  formatCamera,
  formatIntList,
  formatKeyList,
  formatOpacityPair,
  formatPair,
  parseBool01,
  parseCamera,
  parseIntList,
  parseKeyList,
  parseNonNegativeInt,
  parseOpacityPair,
  splitSearch,
} from "./codecs";
import {
  COLOR_BY_VALUES,
  DEFAULT_BASEMAP,
  DEFAULT_CAMERA,
  GRAPH_MODE,
  MAP_EXPLORER_MODE,
  getDefaultLayerVisibility,
  getDefaultOpacity,
  getUrlDefaults,
} from "./defaults";

/**
 * Every query key owned by the map URL state, in serialization order.
 */
export const OWNED_KEYS = [
  "mode",
  "locType",
  "group",
  "params",
  "xparams",
  "telem",
  "active",
  "ccbwqa",
  "flow",
  "analysis",
  "minRecords",
  "colorBy",
  "loc",
  "panel",
  "legend",
  "on",
  "off",
  "opacity",
  "basemap",
  "map",
];

const OWNED_KEY_SET = new Set(OWNED_KEYS);

/** Keys that only apply in graph-mode. */
const GRAPH_ONLY_KEYS = new Set([
  "params",
  "xparams",
  "flow",
  "analysis",
  "minRecords",
  "loc",
  "panel",
  "legend",
]);

/** Keys grouped by the section of state that produces them. */
const SECTION_KEYS = {
  filters: [
    "locType",
    "group",
    "params",
    "xparams",
    "telem",
    "active",
    "ccbwqa",
    "flow",
    "analysis",
    "minRecords",
    "colorBy",
  ],
  location: ["loc"],
  layers: ["on", "off", "opacity"],
  basemap: ["basemap"],
  camera: ["map"],
};

const MODE_URL_VALUE = "wq";
const BASEMAP_KEYS = new Set(BASEMAP_STYLES.map((b) => b.style));

const optionValues = (filtersData, key) =>
  ((filtersData && filtersData[key]) || []).map((option) => option.value);

/* -------------------------------------------------------------------------- */
/* Stage 1: syntactic parse                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Purely syntactic parse of a search string. No option lists are needed.
 * Absent params are `undefined` in `raw`; malformed values are dropped and
 * reported in `dropped` as `{ key, value }`.
 */
export const readRawMapUrl = (search) => {
  const params = new URLSearchParams(search || "");
  const dropped = [];
  const drop = (key, value) => dropped.push({ key, value });

  // Mode is decided first; it scopes everything else
  let mode = MAP_EXPLORER_MODE;
  if (params.has("mode")) {
    const value = params.get("mode");
    if (value === MODE_URL_VALUE) mode = GRAPH_MODE;
    else drop("mode", value);
  }
  const isGraph = mode === GRAPH_MODE;

  const raw = {
    mode,
    locationTypes: undefined,
    parameterGroups: undefined,
    parameters: undefined,
    hasTelem: undefined,
    isActive: undefined,
    isCcbwqa: undefined,
    flow: undefined,
    analysis: undefined,
    minRecords: undefined,
    colorBy: undefined,
    locationId: undefined,
    resultsPanel: undefined,
    legend: undefined,
    layersOn: undefined,
    layersOff: undefined,
    opacity: undefined,
    basemap: undefined,
    camera: undefined,
  };

  const readStringList = (key) => {
    if (!params.has(key)) return undefined;
    const values = [];
    params.getAll(key).forEach((value) => {
      if (value !== "" && !values.includes(value)) values.push(value);
    });
    return values;
  };

  const readBool = (key) => {
    if (!params.has(key)) return undefined;
    const value = params.get(key);
    const parsed = parseBool01(value);
    if (parsed === undefined) drop(key, value);
    return parsed;
  };

  const readNonEmptyString = (key) => {
    if (!params.has(key)) return undefined;
    const value = params.get(key);
    if (value === "") {
      drop(key, value);
      return undefined;
    }
    return value;
  };

  const readInt = (key) => {
    if (!params.has(key)) return undefined;
    const value = params.get(key);
    const parsed = parseNonNegativeInt(value);
    if (parsed === undefined) drop(key, value);
    return parsed;
  };

  raw.locationTypes = readStringList("locType");
  raw.parameterGroups = readStringList("group");

  if (isGraph) {
    // a list made only of malformed ids is treated as absent, not as empty
    if (params.has("params")) {
      const { values, invalid } = parseIntList(params.get("params"));
      invalid.forEach((token) => drop("params", token));
      if (values.length || !invalid.length) raw.parameters = values;
    } else if (params.has("xparams")) {
      const { values, invalid } = parseIntList(params.get("xparams"));
      invalid.forEach((token) => drop("xparams", token));
      if (values.length || !invalid.length) {
        raw.parameters = { exclude: values };
      }
    }
  }

  raw.hasTelem = readBool("telem");
  raw.isActive = readBool("active");
  raw.isCcbwqa = readBool("ccbwqa");

  if (isGraph) {
    raw.flow = readNonEmptyString("flow");
    raw.analysis = readNonEmptyString("analysis");
    raw.minRecords = readInt("minRecords");
  }

  if (params.has("colorBy")) {
    const value = params.get("colorBy");
    const valid =
      COLOR_BY_VALUES.includes(value) && (isGraph || value !== "wqData");
    if (valid) raw.colorBy = value;
    else drop("colorBy", value);
  }

  if (isGraph) {
    raw.locationId = readInt("loc");
    raw.resultsPanel = readBool("panel");
    raw.legend = readBool("legend");
  }

  if (params.has("on")) raw.layersOn = parseKeyList(params.get("on"));
  if (params.has("off")) raw.layersOff = parseKeyList(params.get("off"));

  if (params.has("opacity")) {
    const opacity = {};
    params.getAll("opacity").forEach((value) => {
      const pair = parseOpacityPair(value);
      if (pair) opacity[pair.id] = pair.value;
      else drop("opacity", value);
    });
    raw.opacity = opacity;
  }

  if (params.has("basemap")) {
    const value = params.get("basemap");
    if (BASEMAP_KEYS.has(value)) raw.basemap = value;
    else drop("basemap", value);
  }

  if (params.has("map")) {
    const value = params.get("map");
    const camera = parseCamera(value);
    if (camera) raw.camera = camera;
    else drop("map", value);
  }

  return { raw, dropped };
};

/* -------------------------------------------------------------------------- */
/* Stage 2+: resolve against real option lists                                */
/* -------------------------------------------------------------------------- */

/**
 * Keep the entries of `values` that exist in `options`, in option order.
 * Unknown entries are reported under `key`.
 */
const resolveList = (values, options, key, dropped) => {
  const optionSet = new Set(options);
  const selected = new Set();
  values.forEach((value) => {
    if (optionSet.has(value)) selected.add(value);
    else dropped.push({ key, value: String(value) });
  });
  return options.filter((option) => selected.has(option));
};

/**
 * Like resolveList, but when the URL listed values and none of them are valid
 * (e.g. a stale link) the defaults are used instead of selecting nothing.
 * An explicitly empty list (`group=`) still selects nothing
 */
const resolveListOrDefault = (values, options, key, dropped, defaults) => {
  const resolved = resolveList(values, options, key, dropped);
  return values.length > 0 && resolved.length === 0 ? defaults : resolved;
};

/**
 * Fill every filter field from the URL value when present, otherwise from
 * the mode defaults. Unknown list entries and enum values are dropped.
 */
export const resolveFilterState = (raw, filtersData) => {
  const dropped = [];
  const defaults = getUrlDefaults(raw.mode, filtersData);
  const pick = (field) =>
    raw[field] === undefined ? defaults[field] : raw[field];

  const locationTypes =
    raw.locationTypes === undefined
      ? defaults.locationTypes
      : resolveListOrDefault(
          raw.locationTypes,
          defaults.locationTypes,
          "locType",
          dropped,
          defaults.locationTypes
        );

  const parameterGroups =
    raw.parameterGroups === undefined
      ? defaults.parameterGroups
      : resolveListOrDefault(
          raw.parameterGroups,
          defaults.parameterGroups,
          "group",
          dropped,
          defaults.parameterGroups
        );

  let parameters = defaults.parameters;
  if (Array.isArray(raw.parameters)) {
    parameters = resolveListOrDefault(
      raw.parameters,
      defaults.parameters,
      "params",
      dropped,
      defaults.parameters
    );
  } else if (raw.parameters && Array.isArray(raw.parameters.exclude)) {
    const excluded = new Set(
      resolveList(
        raw.parameters.exclude,
        defaults.parameters,
        "xparams",
        dropped
      )
    );
    parameters = defaults.parameters.filter((id) => !excluded.has(id));
  }

  const resolveEnum = (field, key, optionsKey) => {
    if (raw[field] === undefined) return defaults[field];
    if (optionValues(filtersData, optionsKey).includes(raw[field])) {
      return raw[field];
    }
    dropped.push({ key, value: raw[field] });
    return defaults[field];
  };

  return {
    value: {
      locationTypes,
      parameterGroups,
      parameters,
      hasTelem: pick("hasTelem"),
      isActive: pick("isActive"),
      isCcbwqa: pick("isCcbwqa"),
      flow: resolveEnum("flow", "flow", "flow"),
      analysis: resolveEnum("analysis", "analysis", "analysis"),
      minRecords: pick("minRecords"),
      colorBy: pick("colorBy"),
    },
    dropped,
  };
};

/**
 * Apply `on`/`off` group keys and opacity overrides to the layer defaults.
 * Unknown group keys and layer ids are dropped.
 */
export const resolveLayerState = (raw, layers) => {
  const dropped = [];
  const visibility = getDefaultLayerVisibility(layers);
  const opacity = getDefaultOpacity(layers);

  const applyVisibility = (keys, urlKey, visible) => {
    (keys || []).forEach((groupKey) => {
      if (Object.prototype.hasOwnProperty.call(visibility, groupKey)) {
        visibility[groupKey] = visible;
      } else {
        dropped.push({ key: urlKey, value: groupKey });
      }
    });
  };
  applyVisibility(raw.layersOn, "on", true);
  applyVisibility(raw.layersOff, "off", false);

  Object.keys(raw.opacity || {}).forEach((layerId) => {
    const value = raw.opacity[layerId];
    if (Object.prototype.hasOwnProperty.call(opacity, layerId)) {
      opacity[layerId] = value;
    } else {
      dropped.push({
        key: "opacity",
        value: formatOpacityPair(layerId, value),
      });
    }
  });

  return { value: { visibility, opacity }, dropped };
};

/**
 * Validate the `loc` param against the known monitoring location ids.
 */
export const resolveLocationId = (raw, locationIds) => {
  if (raw.locationId === undefined) return { value: undefined, dropped: [] };
  if (locationIds && locationIds.has(raw.locationId)) {
    return { value: raw.locationId, dropped: [] };
  }
  return {
    value: undefined,
    dropped: [{ key: "loc", value: String(raw.locationId) }],
  };
};

/* -------------------------------------------------------------------------- */
/* Serialize                                                                  */
/* -------------------------------------------------------------------------- */

const sameSet = (a, b) => {
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size !== setB.size) return false;
  return Array.from(setA).every((value) => setB.has(value));
};

/**
 * Order `values` by their position in `options`; unknown values go last in
 * their given order. Duplicates are removed.
 */
const orderByOptions = (values, options) => {
  const unique = Array.from(new Set(values));
  const index = new Map(options.map((option, i) => [option, i]));
  const known = unique
    .filter((value) => index.has(value))
    .sort((a, b) => index.get(a) - index.get(b));
  const unknown = unique.filter((value) => !index.has(value));
  return [...known, ...unknown];
};

const serializeFilters = (filters, filtersData, mode, out) => {
  const isGraph = mode === GRAPH_MODE;
  const defaults = getUrlDefaults(mode, filtersData);

  const writeStringList = (key, values, options) => {
    if (!Array.isArray(values) || sameSet(values, options)) return;
    const ordered = orderByOptions(values, options);
    out[key] = ordered.length ? ordered : [""];
  };
  writeStringList("locType", filters.locationTypes, defaults.locationTypes);
  writeStringList("group", filters.parameterGroups, defaults.parameterGroups);

  if (isGraph && Array.isArray(filters.parameters)) {
    const all = defaults.parameters;
    if (!sameSet(filters.parameters, all)) {
      const selected = orderByOptions(filters.parameters, all);
      const selectedSet = new Set(selected);
      const hasUnknown = selected.some((id) => !all.includes(id));
      const excluded = all.filter((id) => !selectedSet.has(id));
      const selectedStr = formatIntList(selected);
      const excludedStr = formatIntList(excluded);
      if (!hasUnknown && excludedStr.length < selectedStr.length) {
        out.xparams = [excludedStr];
      } else {
        out.params = [selectedStr];
      }
    }
  }

  const writeBool = (key, value, defaultValue) => {
    if (typeof value === "boolean" && value !== defaultValue) {
      out[key] = [formatBool01(value)];
    }
  };
  writeBool("telem", filters.hasTelem, defaults.hasTelem);
  writeBool("active", filters.isActive, defaults.isActive);
  writeBool("ccbwqa", filters.isCcbwqa, defaults.isCcbwqa);

  if (isGraph) {
    if (filters.flow && filters.flow !== defaults.flow) {
      out.flow = [filters.flow];
    }
    if (filters.analysis && filters.analysis !== defaults.analysis) {
      out.analysis = [filters.analysis];
    }
    const minRecords = Number(filters.minRecords);
    if (
      filters.minRecords !== undefined &&
      filters.minRecords !== null &&
      Number.isInteger(minRecords) &&
      minRecords >= 0 &&
      minRecords !== defaults.minRecords
    ) {
      out.minRecords = [String(minRecords)];
    }
  }

  const { colorBy } = filters;
  if (
    COLOR_BY_VALUES.includes(colorBy) &&
    colorBy !== defaults.colorBy &&
    (isGraph || colorBy !== "wqData")
  ) {
    out.colorBy = [colorBy];
  }
};

const serializeLayers = (layers, defaultLayers, out) => {
  const defaultVisibility = getDefaultLayerVisibility(defaultLayers);
  const visibility = layers.visibility || {};
  const on = [];
  const off = [];
  Object.keys(defaultVisibility).forEach((groupKey) => {
    const value = visibility[groupKey];
    if (typeof value !== "boolean" || value === defaultVisibility[groupKey]) {
      return;
    }
    (value ? on : off).push(groupKey);
  });
  if (on.length) out.on = [formatKeyList(on)];
  if (off.length) out.off = [formatKeyList(off)];

  const defaultOpacity = getDefaultOpacity(defaultLayers);
  const opacity = layers.opacity || {};
  const pairs = [];
  Object.keys(defaultOpacity).forEach((layerId) => {
    const value = opacity[layerId];
    if (typeof value !== "number" || !Number.isFinite(value)) return;
    if (Math.abs(value - defaultOpacity[layerId]) > 0.005) {
      pairs.push(formatOpacityPair(layerId, value));
    }
  });
  if (pairs.length) out.opacity = pairs;
};

/**
 * Serialize map state to a search string without a leading "?".
 *
 * Sections whose input is not ready yet (undefined) copy their owned keys
 * from `existingSearch` unchanged. Foreign keys from `existingSearch` are
 * preserved, in their original order, after the owned keys.
 */
export const serializeMapUrl = (current, ctx, existingSearch) => {
  const { filtersData, defaultLayers } = ctx || {};
  const mode = current.mode === GRAPH_MODE ? GRAPH_MODE : MAP_EXPLORER_MODE;
  const isGraph = mode === GRAPH_MODE;
  const segments = splitSearch(existingSearch);

  // key -> array of decoded values to encode
  const computed = {};
  // key -> array of raw segments copied from existingSearch
  const passthrough = {};

  const copySection = (section) => {
    SECTION_KEYS[section].forEach((key) => {
      if (!isGraph && GRAPH_ONLY_KEYS.has(key)) return;
      const raws = segments
        .filter((segment) => segment.key === key)
        .filter((segment) => {
          if (isGraph || key !== "colorBy") return true;
          return new URLSearchParams(segment.raw).get("colorBy") !== "wqData";
        })
        .map((segment) => segment.raw);
      if (raws.length) passthrough[key] = raws;
    });
  };

  if (isGraph) computed.mode = [MODE_URL_VALUE];

  if (current.filters && filtersData) {
    serializeFilters(current.filters, filtersData, mode, computed);
  } else {
    copySection("filters");
  }

  if (isGraph) {
    const { locationId, resultsPanel, legend } = current;
    // undefined = not resolved yet (keep the existing value), null = none
    if (locationId === undefined) {
      copySection("location");
    } else if (Number.isInteger(locationId) && locationId >= 0) {
      computed.loc = [String(locationId)];
    }
    if (resultsPanel === false) computed.panel = [formatBool01(false)];
    if (legend === false) computed.legend = [formatBool01(false)];
  }

  if (current.layers && defaultLayers) {
    serializeLayers(current.layers, defaultLayers, computed);
  } else {
    copySection("layers");
  }

  if (current.basemap !== undefined) {
    if (
      current.basemap !== DEFAULT_BASEMAP &&
      BASEMAP_KEYS.has(current.basemap)
    ) {
      computed.basemap = [current.basemap];
    }
  } else {
    copySection("basemap");
  }

  if (current.camera) {
    const cameraStr = formatCamera(current.camera);
    if (cameraStr !== formatCamera(DEFAULT_CAMERA)) computed.map = [cameraStr];
  } else {
    copySection("camera");
  }

  const parts = [];
  OWNED_KEYS.forEach((key) => {
    if (computed[key]) {
      computed[key].forEach((value) => parts.push(formatPair(key, value)));
    } else if (passthrough[key]) {
      parts.push(...passthrough[key]);
    }
  });
  segments
    .filter((segment) => !OWNED_KEY_SET.has(segment.key))
    .forEach((segment) => parts.push(segment.raw));

  return parts.join("&");
};
